/**
 * SPA API client: base URL points to backend. Paths use /v1/... (no /api prefix).
 * REACT_APP_API_URL is injected by webpack DefinePlugin.
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
export function getApiBaseUrl() {
    // Exact identifier process.env.REACT_APP_API_URL so webpack DefinePlugin replaces it at build time
    const url = process.env.REACT_APP_API_URL;
    if (!url)
        return "http://localhost:8081";
    return String(url).replace(/\/+$/, "");
}
/**
 * Convert relative path (as used in current codebase) to full backend URL.
 * - "/api/doctors/me" -> "{base}/v1/doctors/me"
 * - "/api/auth/email/send-code" -> "{base}/v1/auth/email/send-code"
 */
export function apiUrl(path) {
    const base = getApiBaseUrl();
    const normalized = path.startsWith("/") ? path : `/${path}`;
    if (normalized.startsWith("/api/")) {
        return `${base}${normalized.replace(/^\/api/, "/v1")}`;
    }
    if (normalized.startsWith("/v1/")) {
        return `${base}${normalized}`;
    }
    return `${base}/v1${normalized}`;
}
export function authHeaders(token) {
    const headers = {
        "Content-Type": "application/json",
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}
/**
 * Fetch with backend base URL and optional Bearer token.
 * Use path as in current app: "/api/doctors/me" or "/v1/doctors/me".
 */
export const TOKEN_REFRESHED_EVENT = "bloom:token-refreshed";
// A refresh token works once. A page sends many requests at the same time, so they all share one
// refresh: a second call with the same token gets 401 and the page falls to the error screen.
let refreshInFlight = null;
export function refreshAccessToken() {
    if (typeof window === "undefined")
        return Promise.resolve(null);
    refreshInFlight !== null && refreshInFlight !== void 0 ? refreshInFlight : (refreshInFlight = requestNewAccessToken().finally(() => {
        refreshInFlight = null;
    }));
    return refreshInFlight;
}
async function requestNewAccessToken() {
    const refresh = localStorage.getItem("refresh_token");
    if (!refresh)
        return null;
    const res = await fetch(apiUrl("/v1/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) {
        // Another tab got there first and left its new tokens.
        const current = localStorage.getItem("refresh_token");
        return current && current !== refresh
            ? localStorage.getItem("access_token")
            : null;
    }
    const data = (await res.json());
    if (!data.access_token)
        return null;
    localStorage.setItem("access_token", data.access_token);
    if (data.refresh_token) {
        localStorage.setItem("refresh_token", data.refresh_token);
    }
    window.dispatchEvent(new Event(TOKEN_REFRESHED_EVENT));
    return data.access_token;
}
export async function apiFetch(path, options = {}) {
    const { token } = options, init = __rest(options, ["token"]);
    const url = apiUrl(path);
    const headers = new Headers(init.headers);
    if (!headers.has("Content-Type") && !(init.body instanceof FormData)) {
        headers.set("Content-Type", "application/json");
    }
    const access = token !== null && token !== void 0 ? token : (typeof window !== "undefined"
        ? localStorage.getItem("access_token")
        : null);
    if (access) {
        headers.set("Authorization", `Bearer ${access}`);
    }
    const response = await fetch(url, Object.assign(Object.assign({}, init), { headers }));
    if (response.status !== 401 ||
        path.includes("/auth/refresh") ||
        path.includes("/auth/logout")) {
        return response;
    }
    // Sent before a refresh finished: the new token is already there.
    const stored = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
    const next = stored && stored !== access ? stored : await refreshAccessToken();
    if (!next)
        return response;
    headers.set("Authorization", `Bearer ${next}`);
    return fetch(url, Object.assign(Object.assign({}, init), { headers }));
}
